import { type Dispatch, type SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  SelectInput,
  ApiErrorText,
  BankCardInput,
  Button,
  Checkbox,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Input,
  Loader,
  AnchorDropdown,
  AssetBaseInfo,
} from '@/shared/components';
import { createRegexInputHandlers, getRequiredLabel } from '@/shared/lib';
import type { CurrencyAssetOption, WithdrawCardFormValues } from '../model';
import ContactBookIcon from '@/shared/assets/contact-book-icon.svg?react';
import type { UseFormReturn } from 'react-hook-form';
import type { CommissionResponse } from '@/shared/types';
import {
  WithdrawAmountFormField,
  WithdrawIncludeFeeFormField,
} from '@/entities/operation';
import type { SavedCard } from '@/entities/card';
import EditIcon from '@/shared/assets/edit-icon.svg?react';

interface WithdrawCardFormProps {
  form: UseFormReturn<WithdrawCardFormValues>;
  watchedValues: WithdrawCardFormValues;
  assetOptions: CurrencyAssetOption[];
  savedCards: SavedCard[];
  selectedAsset?: CurrencyAssetOption;
  hasWithdrawFee: boolean;
  isLoading: boolean;
  isError: boolean;
  error?: unknown | null;
  onMaxClick: () => void;
  onEditSavedCard: (savedCard: SavedCard) => void;
  onFormDirtyChange: (isDirty: boolean) => void;
  commissionData?: CommissionResponse;
  cardIconConfig: Record<string, string>;
  setTemplateId: Dispatch<SetStateAction<string | undefined>>;
}

export function WithdrawCardForm({
  form,
  assetOptions,
  savedCards,
  selectedAsset,
  hasWithdrawFee,
  isLoading,
  isError,
  error,
  onMaxClick,
  onFormDirtyChange,
  watchedValues,
  onEditSavedCard,
  cardIconConfig,
  setTemplateId,
}: WithdrawCardFormProps) {
  const { t } = useTranslation();

  const [disabledHolderName, setDisabledHolderName] = useState(false);

  useEffect(() => {
    const formValues = form.getValues();
    const hasAnyFieldFilled = Object.values(formValues).some(value => {
      if (typeof value === 'string') {
        return value.trim() !== '';
      }

      return value;
    });

    onFormDirtyChange(hasAnyFieldFilled);
  }, [watchedValues, onFormDirtyChange]);

  if (isLoading) {
    return <Loader />;
  }

  if (isError) {
    return <ApiErrorText error={error} size="small" />;
  }

  return (
    <div className="flex flex-col gap-[1.6rem]">
      <Form {...form}>
        <form className="flex flex-col gap-[1.6rem]">
          <FormField
            control={form.control}
            name="currencyCode"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <SelectInput
                    {...field}
                    floatingLabel={getRequiredLabel(
                      t('withdrawMethods.crypto.assetLabel')
                    )}
                    options={assetOptions}
                    valueKey="code"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex flex-col gap-[0.4rem]">
            <FormField
              control={form.control}
              name="cardNumber"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <AnchorDropdown<SavedCard>
                      triggerIcon={
                        <ContactBookIcon className="size-[2.4rem]" />
                      }
                      options={savedCards ?? []}
                      getOptionKey={o => o.templateId}
                      contentClassName="max-h-[28.8rem]"
                      renderOption={option => (
                        <div className="py-[1.4rem] px-[2.4rem] flex items-center justify-between min-h-[7.2rem] rounded-[1.6rem] hover:bg-secondary-bg">
                          <AssetBaseInfo
                            asset={{
                              icon: option.cardTypeIcon,
                              code: option.templateName,
                              name: option.cardNumber,
                            }}
                            className="max-w-[calc(100%-1.6rem-4.4rem)]"
                          />

                          <Button
                            variant="icon"
                            size="icon-button"
                            className="w-[4.4rem] h-[4.4rem] hover:bg-secondary-bg text-tertiary hover:text-primary"
                            onClick={() => onEditSavedCard(option)}>
                            <EditIcon className="size-[2.4rem]" />
                          </Button>
                        </div>
                      )}
                      onSelect={option => {
                        form.setValue('cardNumber', option.cardNumber);
                        form.setValue('cardholderName', option.cardholderName);
                        form.setValue('isCardholderCard', true);
                        form.setValue('userCardId', option.userCardId);
                        setTemplateId(option.templateId);
                        setDisabledHolderName(true);
                      }}>
                      {trigger => (
                        <BankCardInput
                          {...field}
                          cardIconConfig={cardIconConfig}
                          type="text"
                          className="p3"
                          showClearButton={false}
                          label={getRequiredLabel(
                            t('withdrawMethods.card.cardNumber')
                          )}
                          onChange={e => {
                            field.onChange(e);
                            form.setValue('userCardId', undefined);
                            setTemplateId('');
                            setDisabledHolderName(false);
                          }}
                          rightContent={trigger}
                        />
                      )}
                    </AnchorDropdown>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {!disabledHolderName && (
              <FormField
                control={form.control}
                name="isCardholderCard"
                render={({ field }) => (
                  <FormItem className="py-[0.8rem]">
                    <label className="flex flex-row items-center gap-[1.6rem] p4 cursor-pointer px-[1.6rem]">
                      <FormControl>
                        <Checkbox
                          className="cursor-pointer dark:aria-invalid:bg-light-error"
                          checked={Boolean(field.value)}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <div>
                        {t('withdrawMethods.card.ownerCardConfirmation')}
                      </div>
                    </label>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
          </div>

          <FormField
            control={form.control}
            name="cardholderName"
            render={({ field }) => {
              const handlers = createRegexInputHandlers(
                /^[A-Za-z ]$/,
                value => {
                  field.onChange(value.toUpperCase());
                }
              );

              return (
                <FormItem>
                  <FormControl>
                    <Input
                      {...field}
                      type="text"
                      disabled={disabledHolderName}
                      className="p3"
                      label={getRequiredLabel(
                        t('withdrawMethods.card.cardHolderName')
                      )}
                      onBeforeInput={handlers.beforeInput}
                      onPaste={handlers.paste}
                      onChange={handlers.change}
                      onBlur={e => {
                        field.onChange(e.currentTarget.value.trim());
                        field.onBlur();
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              );
            }}
          />

          <WithdrawAmountFormField
            control={form.control}
            name="amount"
            selectedAsset={selectedAsset}
            onMaxClick={onMaxClick}
            availableBalanceTranslationKey="withdrawMethods.card.availableBalance"
          />

          <WithdrawIncludeFeeFormField
            control={form.control}
            name="includeFee"
            visible={hasWithdrawFee}
          />
        </form>
      </Form>
    </div>
  );
}
